<template>
  <nav class="flex flex-row items-center">
    <UiLink id="home-breadcrumb" to="/" class="flex flex-row items-center !text-neutral-500" :underline="false">
      <IconHome class="mr-2 size-6 fill-neutral-500" />{{ $t('breadcrumb.home') }}
    </UiLink>
    <template v-for="(item, i) in breadcrumbItems" :key="item.id">
      <IconBullet class="mx-2 size-6 fill-neutral-500" />
      <UiLink
        :id="`breadcrumb-${item.id}`"
        :underline="false"
        :class="route.params.slug && i === route.params.slug.length - 1 ? '!text-neutral-900' : '!text-neutral-500'"
        :to="route.params.slug && i === route.params.slug.length - 1 ? undefined : item.url"
      >
        {{ item.label }}
      </UiLink>
    </template>
  </nav>
</template>

<script setup lang="ts">
import type { BreadcrumbItem } from '~/components/ui/Breadcrumb/types';

import { useCategoryStore } from '~/stores/category/categoryStore';
import { useProductStore } from '~/stores/product/productStore';

const route = useRoute();
const { locale, t } = useI18n();
const runtimeConfig = useRuntimeConfig();
const localePath = useLocalePath();

const categoryStore = useCategoryStore();
const productStore = useProductStore();

const breadcrumbItems = ref<BreadcrumbItem[]>([]);

watch(
  () => route.params.slug,
  () => {
    breadcrumbItems.value = [];
    if (Array.isArray(route.params.slug)) {
      for (const slug of route.params.slug) {
        const category = categoryStore.getCategoryBySlug(slug, locale.value as Locale);

        if (category) {
          breadcrumbItems.value = [
            ...breadcrumbItems.value,
            {
              id: category.id,
              label: category.name[locale.value as Locale],
              url: `/${route.params.slug.slice(0, route.params.slug.indexOf(slug) + 1).join('/')}`,
            },
          ];
        } else if (productStore.product?.slug[locale.value as Locale] === slug) {
          breadcrumbItems.value = [
            ...breadcrumbItems.value,
            {
              id: productStore.product.id,
              label: productStore.product.name,
              url: `/${route.params.slug.join('/')}`,
            },
          ];
        }
      }
    }
  },
  { immediate: true },
);

useJsonld(() => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@id': `${runtimeConfig.public.baseUrl}${localePath('/')}`,
      '@type': 'ListItem',
      name: t('breadcrumb.home'),
      position: 1,
    },
    ...breadcrumbItems.value.map((item, index) => ({
      '@id': `${runtimeConfig.public.baseUrl}${localePath(item.url)}`,
      '@type': 'ListItem',
      name: item.label,
      position: index + 2,
    })),
  ],
}));
</script>
